import Axios from '@/utils/axios';
import { ClientProfile } from '@/types/auth';

// ─── Client-user auth API ─────────────────────────────────────────────
// Mirrors next-portal-v2-web/infrastructure/api/auth/auth.ts (signIn).
// Endpoint: POST {REACT_APP_API_URL}/client-user/v1/auth/login
//   body: { identifier, credential, provider }
//   response envelope: { data: { accessToken, ... } }

export type AuthProvider = 'EMAIL_PASSWORD' | 'GOOGLE' | 'WHATSAPP';

export interface SignInRequest {
  identifier: string; // email
  credential: string; // password
  provider: AuthProvider;
}

export interface SignInResponse {
  data: {
    accessToken: string;
    refreshToken?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Matches next-portal's authProvider.EMAIL constant.
export const authProvider = {
  EMAIL: 'EMAIL_PASSWORD',
  GOOGLE: 'GOOGLE',
  WHATSAPP: 'WHATSAPP',
} as const;

// Headers the client-user gateway expects on every request (next-portal-v2
// sends these via its axios interceptor). `X-Api-Mode` selects live/sandbox.
const CHANNEL_HEADERS = { 'X-Channel': 'LINK' } as const;
const CHANNEL_HEADERS_LIVE = {
  ...CHANNEL_HEADERS,
  'X-Api-Mode': 'live',
} as const;

export const signIn = async (data: SignInRequest): Promise<SignInResponse> => {
  const response = await Axios.post<SignInResponse>(
    '/client-user/v1/auth/login',
    data,
    { headers: CHANNEL_HEADERS }
  );
  return response.data;
};

export interface ClientMeResponse {
  status: number;
  message: string;
  data: ClientProfile;
}

// GET /client-user/v1/clients/me — the authenticated client's own profile.
// Bearer token is attached by the axios interceptor.
export const getClientMe = async (): Promise<ClientProfile> => {
  const response = await Axios.get<ClientMeResponse>(
    '/client-user/v1/clients/me',
    // This endpoint rejects requests missing X-Channel + X-Api-Mode.
    { headers: CHANNEL_HEADERS_LIVE }
  );
  return response.data.data;
};

export const signOutClient = async () => {
  const response = await Axios.delete('/client-user/v1/auth/logout', {
    headers: CHANNEL_HEADERS,
  });
  return response.data;
};

// ─── Price bounds (client capabilities) ───────────────────────────────
// GET /client-user/v1/clients/me/price-bounds — per next-portal, this returns
// the service tiers the client is entitled to, each with its weight/distance
// caps. Shape: { data: { <group>: { <SERVICE_TYPE>: { maxWeightInGrams,
// maxDistanceInMeters } } } } (e.g. group "EXPRESS" → { INSTANT, SAME_DAY }).
// The gateway 400s without a client timezone header.
export interface PriceBoundEntry {
  maxWeightInGrams: number;
  maxDistanceInMeters: number;
}
export interface PriceBoundsResponse {
  status: number;
  message: string;
  data: Record<string, Record<string, PriceBoundEntry>>;
}

export interface ClientServiceCapabilities {
  serviceTypes: string[]; // available service tiers (e.g. ['INSTANT','SAME_DAY'])
  maxWeightKg: number | null; // highest allowed per-shipment weight, null if unknown
}

const resolveClientTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jakarta';
  } catch {
    return 'Asia/Jakarta';
  }
};

// Never throws — the caller falls back to its default tiers on error so a
// failed lookup can't block the form.
export const getClientServiceCapabilities =
  async (): Promise<ClientServiceCapabilities> => {
    try {
      const response = await Axios.get<PriceBoundsResponse>(
        '/client-user/v1/clients/me/price-bounds',
        {
          headers: {
            ...CHANNEL_HEADERS_LIVE,
            'x-client-time-zone': resolveClientTimeZone(),
          },
        }
      );
      const groups = response.data?.data ?? {};
      // Prefer the EXPRESS group (what next-portal reads); fall back to merging
      // every group so a differently-keyed client still gets its tiers.
      const entries = Object.entries(groups.EXPRESS ?? {}).length
        ? { EXPRESS: groups.EXPRESS }
        : groups;
      const serviceTypes = new Set<string>();
      let maxWeightInGrams = 0;
      for (const tiers of Object.values(entries)) {
        for (const [tier, bound] of Object.entries(tiers ?? {})) {
          serviceTypes.add(tier);
          if (bound?.maxWeightInGrams > maxWeightInGrams)
            maxWeightInGrams = bound.maxWeightInGrams;
        }
      }
      return {
        serviceTypes: Array.from(serviceTypes),
        maxWeightKg: maxWeightInGrams > 0 ? maxWeightInGrams / 1000 : null,
      };
    } catch {
      return { serviceTypes: [], maxWeightKg: null };
    }
  };
